import React from 'react';
import { X, Clock, User, AlertCircle, ArrowRight } from 'lucide-react';

const surveillanceColors = { 
  vert: "#22c55e", 
  jaune: "#facc15", 
  orange: "#fb923c", 
  rouge: "#ef4444" 
};

const SurveillanceHistoryModal = ({ isOpen, onClose, history = [] }) => {
  if (!isOpen) return null;

  const getStateLabel = (state) => {
    const labels = {
      vert: "Normal",
      jaune: "Attention", 
      orange: "Vigilance",
      rouge: "Critique"
    };
    return labels[state] || state;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Trier l'historique par date décroissante (plus récent en premier)
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = new Date(a.date || 0);
    const dateB = new Date(b.date || 0);
    return dateB - dateA;
  });

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-600" />
            Historique de surveillance
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          {sortedHistory.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun historique de surveillance</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedHistory.map((entry, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  {/* Date et acteur */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(entry.date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-3 w-3" />
                      <span>{entry.acteur || 'Utilisateur'}</span>
                    </div>
                  </div>

                  {/* Changement d'état */}
                  <div className="flex items-center gap-2 mb-2">
                    {entry.ancienEtat && (
                      <>
                        <div 
                          className="px-2 py-1 rounded-full text-white text-xs font-medium"
                          style={{ backgroundColor: surveillanceColors[entry.ancienEtat] }}
                        >
                          {getStateLabel(entry.ancienEtat)}
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                      </>
                    )}
                    <div 
                      className="px-2 py-1 rounded-full text-white text-xs font-medium"
                      style={{ backgroundColor: surveillanceColors[entry.etat] }}
                    >
                      {getStateLabel(entry.etat)}
                    </div>
                  </div>

                  {/* Messages */}
                  {(entry.message || entry.ancienMessage) && (
                    <div className="space-y-1">
                      {entry.ancienMessage && entry.ancienMessage !== entry.message && (
                        <div className="text-sm">
                          <span className="text-gray-500">Ancien message:</span>
                          <p className="text-gray-600 bg-white p-2 rounded mt-1 line-through">
                            {entry.ancienMessage}
                          </p>
                        </div>
                      )}
                      {entry.message && (
                        <div className="text-sm">
                          <span className="text-gray-500">
                            {entry.ancienMessage && entry.ancienMessage !== entry.message ? 'Nouveau message:' : 'Message:'}
                          </span>
                          <p className="text-gray-700 bg-white p-2 rounded mt-1">
                            {entry.message}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Type d'action */}
                  {entry.action && (
                    <div className="mt-2 text-xs text-gray-500 italic">
                      Action: {entry.action === 'creation' ? 'Création' : 
                               entry.action === 'modification' ? 'Modification' : 
                               entry.action}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            type="button"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveillanceHistoryModal;